import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { useThemeTokens } from '@providers/themeTokenProvider';
import {
  BLOTTER_LABEL_SMALL_SX,
  LABEL_SX,
  MICRO_SX,
  VALUE_SX,
} from '../styles';
import { formatMoney } from '../utils';

type SAColumnHeaderProps = {
  saId: string;
  label: string;
  accountType: string;
  accountBaseCurrency?: string;
  expectedCashCents: number;
  cashAvailableForTradeCents: number;
  marketValueCents: number;
  remainingCash: number;
  hasTradesInSA: boolean;
  multiSA: boolean;
  isLast: boolean;
  gridColumnStart: number;
  gridColumnEnd: number;
  saSeparator: any;
  idx: number;
};

const SAColumnHeader = ({
  label,
  accountBaseCurrency,
  remainingCash,
  hasTradesInSA,
  multiSA,
}: SAColumnHeaderProps) => {
  const { sys } = useThemeTokens();

  const isOverdrawn = remainingCash < -100;
  const isLow = remainingCash >= -100 && remainingCash < 500;
  const cashColor = isOverdrawn
    ? sys.color.negative
    : isLow
      ? sys.color.warning
      : sys.color.positive;

  return (
    <Box
      sx={{
        py: 0.375,
        px: 0.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 0.5,
      }}
    >
      <Typography
        variant='labelSmall'
        sx={{
          ...LABEL_SX,
          fontWeight: 700,
          letterSpacing: '0.04em',
          color: sys.color.onSurface,
        }}
      >
        {multiSA ? label : 'Trade $'}
      </Typography>
      <Box display='flex' alignItems='center' gap={0.5}>
        {accountBaseCurrency && (
          <Typography
            variant='labelSmall'
            sx={{
              ...MICRO_SX,
              fontWeight: 600,
              color: sys.color.onSurfaceVariant,
            }}
          >
            {accountBaseCurrency}
          </Typography>
        )}
        {hasTradesInSA && (
          <Typography
            variant='labelSmall'
            sx={{
              ...LABEL_SX,
              fontWeight: 700,
              color: cashColor,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatMoney(remainingCash)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default SAColumnHeader;

export type AccountSummaryData = {
  id: string;
  label: string;
  accountType: string;
  accountBaseCurrency?: string;
  expectedCashCents: number;
  cashAvailableForTradeCents: number;
  marketValueCents: number;
  remainingCash: number;
  hasTradesInSA: boolean;
};

export const AccountSummaryStrip = ({
  accounts,
  multiSA,
}: {
  accounts: AccountSummaryData[];
  multiSA: boolean;
}) => {
  const { sys } = useThemeTokens();

  return (
    <Box
      sx={{
        px: 2,
        py: 0.5,
        borderBottom: '2px solid',
        borderColor: sys.color.outlineVariant,
        display: 'flex',
        alignItems: 'center',
        gap: multiSA ? 2 : 1.5,
        flexShrink: 0,
        bgcolor: sys.color.surface,
      }}
    >
      {accounts.map((sa) => {
        const isOverdrawn = sa.remainingCash < -100;
        const isLow = sa.remainingCash >= -100 && sa.remainingCash < 500;
        const cashColor = isOverdrawn
          ? sys.color.negative
          : isLow
            ? sys.color.warning
            : sys.color.positive;
        const cashRatio =
          sa.expectedCashCents > 0
            ? sa.remainingCash / sa.expectedCashCents
            : 1;
        const healthPct = Math.max(0, Math.min(100, cashRatio * 100));
        const healthColor =
          cashRatio > 1
            ? sys.color.positive
            : cashRatio > 0.5
              ? sys.color.warning
              : sys.color.negative;
        const saMV = sa.marketValueCents || 1;
        const currentCashPct = (sa.cashAvailableForTradeCents / saMV) * 100;
        const projectedCashPct = (sa.remainingCash / saMV) * 100;

        return (
          <Box
            key={sa.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flex: multiSA ? 1 : undefined,
              minWidth: 0,
            }}
          >
            {multiSA && (
              <Typography
                variant='labelSmall'
                sx={{
                  ...LABEL_SX,
                  fontWeight: 700,
                  color: sys.color.onSurface,
                  whiteSpace: 'nowrap',
                }}
              >
                {sa.label}
              </Typography>
            )}
            <Box display='flex' alignItems='center' gap={0.75}>
              <Box>
                <Typography
                  variant='labelSmall'
                  sx={{
                    ...BLOTTER_LABEL_SMALL_SX,
                    color: sys.color.onDisabled,
                    lineHeight: 1,
                  }}
                >
                  TARGET
                </Typography>
                <Typography
                  variant='labelSmall'
                  sx={{
                    ...VALUE_SX,
                    fontWeight: 700,
                    color: sys.color.onSurface,
                    whiteSpace: 'nowrap',
                    lineHeight: 1.3,
                  }}
                >
                  {formatMoney(sa.expectedCashCents)}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant='labelSmall'
                  sx={{
                    ...BLOTTER_LABEL_SMALL_SX,
                    color: sys.color.onDisabled,
                    lineHeight: 1,
                  }}
                >
                  {sa.hasTradesInSA ? 'AVAILABLE' : 'CASH'}
                </Typography>
                <Typography
                  variant='labelSmall'
                  sx={{
                    ...VALUE_SX,
                    fontWeight: 700,
                    color: cashColor,
                    whiteSpace: 'nowrap',
                    lineHeight: 1.3,
                    transition: 'color 0.3s ease',
                  }}
                >
                  {formatMoney(sa.remainingCash)}
                </Typography>
              </Box>
              {sa.hasTradesInSA && (
                <Box display='flex' alignItems='center' gap={0.4}>
                  <Typography
                    variant='labelSmall'
                    sx={{
                      ...MICRO_SX,
                      fontWeight: 600,
                      color: sys.color.onSurfaceVariant,
                    }}
                  >
                    {currentCashPct.toFixed(1)}%
                  </Typography>
                  <Typography
                    variant='labelSmall'
                    sx={{ fontSize: '0.5rem', color: sys.color.onDisabled }}
                  >
                    →
                  </Typography>
                  <Typography
                    variant='labelSmall'
                    sx={{
                      ...MICRO_SX,
                      fontWeight: 700,
                      color:
                        projectedCashPct < 1
                          ? sys.color.negative
                          : projectedCashPct < 3
                            ? sys.color.warning
                            : sys.color.onSurfaceVariant,
                    }}
                  >
                    {projectedCashPct.toFixed(1)}%
                  </Typography>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                width: 48,
                height: 4,
                borderRadius: '2px',
                bgcolor: sys.color.neutralHover,
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${healthPct}%`,
                  bgcolor: healthColor,
                  borderRadius: '2px',
                  transition: 'width 0.3s ease, background 0.3s ease',
                }}
              />
            </Box>
            {sa.accountBaseCurrency && (
              <Typography
                variant='labelSmall'
                sx={{
                  ...MICRO_SX,
                  fontWeight: 600,
                  color: sys.color.onDisabled,
                }}
              >
                {sa.accountBaseCurrency}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
